import { ChevronRight, Hand, Lock, ShieldCheck, ShieldX } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { formatDuration } from '../lib/format.ts';
import { StatusPill } from './StatusPill.tsx';
import type { PillStatus } from './StatusPill.tsx';

export interface StageGate {
  /** "Approval from Srikanth", "Tests must pass". */
  label: string;
  state: 'waiting' | 'passed' | 'blocked' | 'locked';
}

export interface PipelineStageProps {
  name: string;
  status: PillStatus;
  /** Override the status words ("Passed", "Running 3 of 5"). */
  statusLabel?: string;
  /** Time spent in the stage, ms. */
  durationMs?: number;
  /** Items in the stage, for boards. */
  count?: number;
  gate?: StageGate;
  /** The stage being looked at. */
  current?: boolean;
  onOpen?: () => void;
  children?: ReactNode;
  className?: string;
}

const GATE = {
  waiting: { icon: Hand, cls: 'text-status-attention', words: 'Waiting' },
  passed: { icon: ShieldCheck, cls: 'text-status-success', words: 'Passed' },
  blocked: { icon: ShieldX, cls: 'text-status-danger', words: 'Blocked' },
  locked: { icon: Lock, cls: 'text-fg-subtle', words: 'Not reached' },
};

/** One stage of a pipeline: name, status, duration and the gate that guards the next stage. */
export function PipelineStage({ name, status, statusLabel, durationMs, count, gate, current, onOpen, children, className }: PipelineStageProps) {
  const g = gate ? GATE[gate.state] : null;
  const GateIcon = g?.icon;
  const content = (
    <>
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-ui font-medium text-fg">{name}</span>
        {count != null && <span className="shrink-0 text-caption text-fg-subtle num">{count}</span>}
      </div>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <StatusPill status={status} label={statusLabel} className="shrink-0" />
        {durationMs != null && <span className="shrink-0 text-caption text-fg-subtle num">{formatDuration(durationMs)}</span>}
      </div>
      {gate && g && GateIcon && (
        <div className="flex min-w-0 items-center gap-1.5 border-t border-border-subtle pt-2 text-caption text-fg-muted">
          <GateIcon className={cn('size-3.5 shrink-0', g.cls)} aria-hidden />
          <span className="truncate" title={gate.label}>
            {gate.label}
          </span>
          <span className={cn('ml-auto shrink-0 font-medium', g.cls)}>{g.words}</span>
        </div>
      )}
      {children}
    </>
  );
  const frame = cn(
    'flex min-w-0 flex-1 flex-col gap-2 rounded-(--stage-radius) border bg-surface p-3 text-left hairline',
    current ? 'border-accent-line' : 'border-border',
    onOpen && 'transition-colors duration-(--duration-fast) hover:border-border-strong hover:bg-raised',
    className,
  );
  if (onOpen) {
    return (
      <button type="button" className={frame} onClick={onOpen} aria-current={current ? 'step' : undefined}>
        {content}
      </button>
    );
  }
  return (
    <div className={frame} aria-current={current ? 'step' : undefined}>
      {content}
    </div>
  );
}

/** Stages left to right with connectors. Wraps on narrow screens. */
export function PipelineStages({ children, label = 'Pipeline stages', className }: { children: ReactNode[]; label?: string; className?: string }) {
  return (
    <ol aria-label={label} className={cn('flex min-w-0 flex-wrap items-stretch gap-y-3', className)}>
      {children.map((child, i) => (
        <li key={i} className="flex min-w-(--stage-min-width) flex-1 items-stretch">
          {child}
          {i < children.length - 1 && <ChevronRight className="mx-1 size-4 shrink-0 self-center text-(--stage-connector)" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}
